/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';

import type {
	useVersionHistoryContentVersionHistoryQuery as useVersionHistoryContentVersionHistoryQuery$data,
	useVersionHistoryContentVersionHistoryQueryVariables as useVersionHistoryContentVersionHistoryQuery$variables,
} from './__types__/useVersionHistoryContentVersionHistoryQuery';

type useVersionHistoryType = {
	contentType: string;
	contentId: string;
};

export const useVersionHistory = ({ contentType, contentId }: useVersionHistoryType) => {
	const { data, loading, error, fetchMore, refetch } = useQuery<
		useVersionHistoryContentVersionHistoryQuery$data,
		useVersionHistoryContentVersionHistoryQuery$variables
	>(
		gql`
			query useVersionHistoryContentVersionHistoryQuery(
				$contentId: ID!
				$filter: ContentVersionHistoryFilter!
				$after: String
			) {
				contentVersionHistory(id: $contentId, after: $after, first: 30, filter: $filter) {
					nodes {
						author {
							id
							isActive
							displayName
						}
						contentId
						number
						updatedTime
						contentProperties {
							latest {
								externalVersionId
								versionContainsAIContent
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
				}
			}
		`,
		{
			variables: { contentId, filter: { contentType } },
		},
	);
	const startCursor = data?.contentVersionHistory?.pageInfo?.startCursor || null;
	const hasNextPage = data?.contentVersionHistory?.pageInfo?.hasNextPage || false;

	const autosavedVersions = data?.contentVersionHistory?.nodes || [];

	const loadMoreVersions = () => {
		if (hasNextPage && startCursor && !loading) {
			void fetchMore({
				variables: { contentId, filter: { contentType }, after: startCursor },
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.contentVersionHistory) {
						return prev;
					}

					const prevNodes = prev?.contentVersionHistory?.nodes || [];
					const fetchedNodes = fetchMoreResult.contentVersionHistory.nodes || [];
					const pageInfo =
						fetchMoreResult.contentVersionHistory.pageInfo || prev?.contentVersionHistory?.pageInfo;

					return {
						contentVersionHistory: {
							nodes: [...prevNodes, ...fetchedNodes],
							pageInfo,
							__typename: 'ContentVersionHistoryConnection',
						},
					};
				},
			});
		}
	};

	return {
		autosavedVersions,
		loading,
		error,
		loadMoreVersions,
		cursor: startCursor,
		hasNextPage,
		refetch,
	};
};
